package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.material3.Text
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextOverflow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import kotlinx.coroutines.delay

/** One rotating line in an [ActivityTicker]: a Material Symbols ligature name + pre-localized text. */
data class ActivityTickerEntry(
    val iconName: String,
    val text: String,
)

/**
 * A thin auto-rotating "what's happening" strip: shows one entry at a time and advances with a
 * vertical slide every few seconds. Hidden entirely when [entries] is empty. Contains no copy —
 * callers pass localized text (Golden rule 6).
 */
@Composable
fun ActivityTicker(
    entries: List<ActivityTickerEntry>,
    modifier: Modifier = Modifier,
) {
    if (entries.isEmpty()) return
    var index by remember(entries.size) { mutableIntStateOf(0) }
    LaunchedEffect(entries.size) {
        while (true) {
            delay(ROTATE_MILLIS)
            index = (index + 1) % entries.size
        }
    }
    AnimatedContent(
        targetState = entries[index % entries.size],
        transitionSpec = {
            (slideInVertically { it } + fadeIn()) togetherWith (slideOutVertically { -it } + fadeOut())
        },
        modifier = modifier
            .fillMaxWidth()
            .clip(AppTheme.shapes.pill)
            .background(AppTheme.colors.surfaceContainer),
    ) { entry ->
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.sm),
        ) {
            AppIcon(
                name = entry.iconName,
                contentDescription = null,
                tint = AppTheme.colors.primary,
                modifier = Modifier.size(AppTheme.sizes.iconSm),
            )
            Text(
                text = entry.text,
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onSurfaceVariant,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.padding(start = AppTheme.spacing.sm),
            )
        }
    }
}

private const val ROTATE_MILLIS = 4_000L
